import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import GoogleMapPicker from '../../components/GoogleMapPicker';
import CheckoutSheet from '../wallet/CheckoutSheet';

const walletLogo = '/assets/images/sysIcons/wallet.svg';
const STEPS   = ['نوع المياه', 'الكمية', 'الموقع', 'التأكيد والسعر'];
const fmtSAR  = v => Number(v).toFixed(2);
const DEFAULT_CENTER = [24.7136, 46.6753]; // Riyadh


/* ════════════════════════════════════════════
   Main Component
════════════════════════════════════════════ */
export default function NewOrderPage() {
  const { apiCall } = useCustomerAuth();
  const toast       = useToast();
  const navigate    = useNavigate();

  /* ── Theme ── */
  const [isDark, setIsDark] = useState(() => document.documentElement.getAttribute('data-theme') !== 'light');
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute('data-theme') !== 'light')
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  /* ── Wallet Top-up Sheet ── */
  const [showTopup, setShowTopup] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  /* ── Init data ── */
  const [step,             setStep]             = useState(0);
  const [waterTypes,       setWaterTypes]       = useState([]);
  const [vehicleCategories,setVehicleCategories]= useState([]);
  const [sites,            setSites]            = useState([]);
  const [loading,          setLoading]          = useState(true);

  /* ── Order data ── */
  const [waterTypeId,   setWaterTypeId]   = useState(null);
  const [selCategory,   setSelCategory]   = useState(null);  // { id, name, capacity_tons }
  const [siteId,        setSiteId]        = useState(null);  // saved address
  const [mapPos,        setMapPos]        = useState(null);  // [lat, lng]
  const [geocodedAddr,  setGeocodedAddr]  = useState('');
  const [notes,         setNotes]         = useState('');

  /* ── Save address prompt ── */
  const [saveAddrName,  setSaveAddrName]  = useState('');
  const [savingAddr,    setSavingAddr]    = useState(false);
  const [addrSaved,     setAddrSaved]     = useState(false);

  /* ── Price estimate ── */
  const [estimate,      setEstimate]      = useState(null);
  const [priceLoading,  setPriceLoading]  = useState(false);

  /* ── Submission / Waiting ── */
  const [submitting,    setSubmitting]    = useState(false);
  const [waiting,       setWaiting]       = useState(false);
  const [waitSecs,      setWaitSecs]      = useState(0);
  const [accepted,      setAccepted]      = useState(false);
  const [orderId,       setOrderId]       = useState(null);
  const pollRef = useRef(null);

  /* ── No-driver / Alternatives (inline) ── */
  const [noDriver,      setNoDriver]      = useState(false);   // order got no_driver_available
  const [altLoading,    setAltLoading]    = useState(false);
  const [altList,       setAltList]       = useState(null);    // null = not loaded yet
  const [acceptingAlt,  setAcceptingAlt]  = useState(false);
  const [cancellingOrd, setCancellingOrd] = useState(false);

  /* ── Payment ── */
  const [paymentMethod,  setPaymentMethod]  = useState('cash');
  const [walletBalance,  setWalletBalance]  = useState(null);

  /* ── Load initial data ── */
  useEffect(() => {
    (async () => {
      try {
        const [wt, vc, s, wal] = await Promise.all([
          apiCall('/customer/catalog/water-types'),
          apiCall('/customer/catalog/vehicle-categories'),
          apiCall('/customer/sites'),
          apiCall('/customer/wallet').catch(() => null),
        ]);
        setWaterTypes(wt);
        setVehicleCategories(vc);
        setSites(s);
        if (wal) setWalletBalance(wal.balance ?? 0);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [apiCall]);

  /* ── Cleanup poll on unmount ── */
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  /* ── Fetch price when entering step 3 ── */
  useEffect(() => {
    if (step !== 3 || !waterTypeId || !selCategory) return;
    setPriceLoading(true);
    setEstimate(null);
    const p = new URLSearchParams({
      water_type_id: waterTypeId,
      quantity_tons: selCategory.capacity_tons,
    });
    apiCall(`/customer/catalog/estimate?${p}`)
      .then(d => setEstimate(d))
      .catch(() => {})
      .finally(() => setPriceLoading(false));
  }, [step]);

  /* ── Auto-select default site when entering step 2 ── */
  useEffect(() => {
    if (step !== 2) return;
    if (mapPos || siteId) return;
    const defaultSite = sites.find(s => s.is_default);
    if (defaultSite) selectSite(defaultSite);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /* ── GoogleMapPicker callback ── */
  const handlePick = useCallback(({ lat, lng, address }) => {
    setMapPos([lat, lng]);
    setGeocodedAddr(address);
    setSiteId(null);
    setAddrSaved(false);
    setSaveAddrName('');
  }, []);

  /* ── Select saved address ── */
  const selectSite = (site) => {
    setSiteId(site.id);
    setMapPos(site.latitude && site.longitude ? [site.latitude, site.longitude] : null);
    setGeocodedAddr(site.formatted_address || site.address_line1 || '');
    setAddrSaved(false);
  };

  /* ── Save new address ── */
  const saveAddress = async () => {
    if (!saveAddrName.trim() || !mapPos) return;
    setSavingAddr(true);
    try {
      const saved = await apiCall('/customer/sites', {
        method: 'POST',
        body: JSON.stringify({
          name: saveAddrName.trim(),
          formatted_address: geocodedAddr,
          latitude: mapPos[0],
          longitude: mapPos[1],
        }),
      });
      setSites(prev => [saved, ...prev]);
      setSiteId(saved.id);
      setAddrSaved(true);
      toast.success('تم حفظ العنوان');
    } catch (e) { toast.error(e.message); }
    finally { setSavingAddr(false); }
  };

  /* ── Polling ── */
  const startPolling = useCallback((id) => {
    let secs = 0;
    const iv = setInterval(async () => {
      secs++;
      setWaitSecs(secs);
      try {
        const d = await apiCall(`/customer/orders/${id}`);

        // Marketplace: offers arrived — redirect to offers page
        if (d.status === 'offers_ready') {
          clearInterval(iv);
          setAccepted(true);
          setTimeout(() => navigate(`/customer/orders/${id}/offers`, { replace: true }), 800);
          return;
        }
        // Marketplace: no offers at all
        if (d.status === 'expired_no_offers') {
          clearInterval(iv);
          navigate(`/customer/orders/${id}`, { replace: true });
          return;
        }
        // Classic flow: no driver
        if (d.status === 'no_driver_available') {
          clearInterval(iv);
          setNoDriver(true);
          setAltLoading(true);
          apiCall(`/customer/orders/${id}/alternatives`)
            .then(r => setAltList(r.alternatives || []))
            .catch(() => setAltList([]))
            .finally(() => setAltLoading(false));
          return;
        }
        if (['assigned','accepted','en_route_pickup','at_source','loading',
             'en_route_delivery','arrived','delivering','completed'].includes(d.status)) {
          clearInterval(iv);
          setAccepted(true);
          setTimeout(() => navigate(`/customer/orders/${id}`, { replace: true }), 1800);
          return;
        }
        if (d.status === 'cancelled') {
          clearInterval(iv);
          navigate(`/customer/orders/${id}`, { replace: true });
          return;
        }
      } catch { /* silent */ }
      if (secs >= 300) {   // marketplace needs more time (5 min)
        clearInterval(iv);
        navigate(`/customer/orders/${id}`, { replace: true });
      }
    }, 2000);  // poll every 2s (not 1s) to reduce load
    pollRef.current = iv;
  }, [apiCall, navigate]);

  /* ── Accept alternative capacity ── */
  const acceptAlternative = async (qty) => {
    setAcceptingAlt(true);
    try {
      await apiCall(`/customer/orders/${orderId}/adjust-capacity`, {
        method: 'POST',
        body: JSON.stringify({ new_quantity: qty }),
      });
      navigate(`/customer/orders/${orderId}`, { replace: true });
    } catch (err) {
      toast.error(err.message || 'تعذر تعديل الطلب');
    } finally {
      setAcceptingAlt(false);
    }
  };

  /* ── Cancel order from waiting screen ── */
  const cancelOrderInline = async () => {
    setCancellingOrd(true);
    try {
      await apiCall(`/customer/orders/${orderId}/cancel`, { method: 'POST' });
      toast.success('تم إلغاء الطلب');
      navigate('/customer/orders', { replace: true });
    } catch (err) {
      toast.error(err.message || 'تعذر إلغاء الطلب');
    } finally {
      setCancellingOrd(false);
    }
  };

  /* ── Submit ── */
  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await apiCall('/customer/orders', {
        method: 'POST',
        body: JSON.stringify({
          site_id:            siteId || null,
          water_type_id:      waterTypeId,
          requested_quantity: selCategory.capacity_tons,
          delivery_address:   siteId ? null : (geocodedAddr || null),
          delivery_lat:       siteId ? null : (mapPos ? mapPos[0] : null),
          delivery_lng:       siteId ? null : (mapPos ? mapPos[1] : null),
          customer_notes:     notes || null,
          payment_method:     paymentMethod,
          best_tenant_id:     estimate?.best_tenant_id || null,
          // best_ship_id intentionally omitted — marketplace assigns ship after bid
        }),
      });

      setOrderId(result.id);
      setWaiting(true);

      // Marketplace — always poll; redirect to offers page when ready
      startPolling(result.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Navigation guards ── */
  const canNext = () => {
    if (step === 0) return !!waterTypeId;
    if (step === 1) return !!selCategory;
    if (step === 2) return !!(siteId || mapPos);
    return true;
  };

  const selectedWaterType = waterTypes.find(w => w.id === waterTypeId);
  const isNewLocation     = !siteId && !!mapPos;

  /* ═══════════════ WAITING SCREEN ═══════════════ */
  if (waiting) {
    /* ── Offers arrived / accepted ── */
    if (accepted) {
      return (
        <div style={{ minHeight: '65vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '1.25rem', padding: '0 1.5rem' }}>
          <div style={{ position: 'relative', width: 88, height: 88 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(16,185,129,0.12)', animation: 'wt-ripple-anim 1.5s ease-out infinite' }} />
            <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '50%', background: 'linear-gradient(135deg, #059669, #10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 32px rgba(16,185,129,0.5)' }}>
              <svg style={{ width: 40, height: 40, color: '#fff' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
            </div>
          </div>
          <div>
            <h2 style={{ color: 'var(--wt-text)', fontWeight: 900, fontSize: '1.35rem', margin: '0 0 0.4rem', fontFamily: "'Cairo', sans-serif" }}>وصلت العروض!</h2>
            <p style={{ color: 'var(--wt-text-3)', fontSize: '0.875rem', margin: 0, fontFamily: "'Cairo', sans-serif" }}>جاري عرض عروض المزودين...</p>
          </div>
          <style>{`@keyframes wt-ripple-anim { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(2.2);opacity:0} }`}</style>
        </div>
      );
    }

    /* ── No driver available ── */
    if (noDriver) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {/* Status banner */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.06))',
            border: '1px solid rgba(245,158,11,0.28)',
            borderRadius: 'var(--wt-radius-md)', padding: '1rem 1.125rem',
            display: 'flex', alignItems: 'flex-start', gap: '0.875rem',
          }}>
            <div style={{ width: 40, height: 40, borderRadius: '0.75rem', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg style={{ width: 20, height: 20, color: '#f59e0b' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><circle cx="6.5" cy="16" r="2.5"/><circle cx="17.5" cy="16" r="2.5"/></svg>
            </div>
            <div>
              <p style={{ color: '#fbbf24', fontWeight: 800, fontSize: '0.875rem', margin: '0 0 0.25rem', fontFamily: "'Cairo', sans-serif" }}>لم نجد سائقاً حالياً</p>
              <p style={{ color: 'var(--wt-text-3)', fontSize: '0.78rem', margin: 0, lineHeight: 1.6, fontFamily: "'Cairo', sans-serif" }}>
                {altLoading ? 'جاري البحث عن كميات متاحة...'
                  : altList && altList.length > 0 ? `وجدنا ${altList.length} كميات متاحة — اختر ما يناسبك`
                  : 'لا توجد شاحنات متاحة حالياً — حاول لاحقاً'}
              </p>
            </div>
          </div>

          {/* Loading alts */}
          {altLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ width: 18, height: 18, border: '2px solid var(--wt-border)', borderTopColor: 'var(--wt-cyan)', borderRadius: '50%', animation: 'wt-spin 0.7s linear infinite' }} />
              <span style={{ color: 'var(--wt-text-3)', fontSize: '0.825rem', fontFamily: "'Cairo', sans-serif" }}>جاري التحميل...</span>
            </div>
          )}

          {/* Alt list */}
          {!altLoading && altList && altList.length > 0 && (
            <div className="wt-glass" style={{ borderRadius: 'var(--wt-radius-md)', overflow: 'hidden' }}>
              <div style={{ padding: '0.625rem 1rem', borderBottom: '1px solid var(--wt-border)' }}>
                <p style={{ color: 'var(--wt-text-3)', fontSize: '0.72rem', fontWeight: 700, margin: 0, fontFamily: "'Cairo', sans-serif" }}>الكميات المتاحة — اختر أو ألغِ</p>
              </div>
              {altList.map((alt) => (
                <div key={alt.capacity_tons} style={{ padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid var(--wt-border)' }}>
                  <div style={{ width: 38, height: 38, borderRadius: '0.75rem', background: 'var(--wt-cyan-pale)', border: '1px solid var(--wt-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg style={{ width: 18, height: 18, color: 'var(--wt-cyan-bright)' }} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z"/></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--wt-text)', fontWeight: 800, fontSize: '0.9rem', fontFamily: "'Cairo', sans-serif" }}>{alt.capacity_tons} طن</span>
                      {alt.capacity_tons < (selCategory?.capacity_tons || 0) && <span style={{ fontSize: '0.65rem', background: 'rgba(245,158,11,0.14)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 999, padding: '0.1rem 0.45rem', fontWeight: 800, fontFamily: "'Cairo', sans-serif" }}>أقل</span>}
                      {alt.capacity_tons > (selCategory?.capacity_tons || 0) && <span style={{ fontSize: '0.65rem', background: 'rgba(14,165,233,0.12)', color: 'var(--wt-cyan-bright)', border: '1px solid var(--wt-border)', borderRadius: 999, padding: '0.1rem 0.45rem', fontWeight: 800, fontFamily: "'Cairo', sans-serif" }}>أكبر</span>}
                    </div>
                    {alt.total != null && (
                      <p style={{ color: 'var(--wt-text-3)', fontSize: '0.72rem', margin: '0.15rem 0 0', fontFamily: "'Cairo', sans-serif" }}>
                        الإجمالي: <span style={{ color: 'var(--wt-emerald)', fontWeight: 700 }} dir="ltr">{Number(alt.total).toFixed(2)}</span>
                        {alt.price_per_ton && <span style={{ color: 'var(--wt-text-3)', marginRight: '0.25rem' }}>({Number(alt.price_per_ton).toFixed(2)}/طن)</span>}
                      </p>
                    )}
                  </div>
                  <button onClick={() => acceptAlternative(alt.capacity_tons)} disabled={acceptingAlt}
                    className="wt-btn-primary" style={{ padding: '0.5rem 0.875rem', fontSize: '0.78rem', borderRadius: '0.75rem', opacity: acceptingAlt ? 0.6 : 1, flexShrink: 0 }}>
                    {acceptingAlt ? '...' : 'قبول'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem' }}>
            <button onClick={cancelOrderInline} disabled={cancellingOrd}
              style={{ width: '100%', padding: '0.875rem', borderRadius: 'var(--wt-radius-md)', border: '1px solid rgba(244,63,94,0.28)', background: 'rgba(244,63,94,0.08)', color: '#fda4af', fontWeight: 800, fontSize: '0.875rem', cursor: 'pointer', fontFamily: "'Cairo', sans-serif", opacity: cancellingOrd ? 0.6 : 1 }}>
              {cancellingOrd ? 'جاري الإلغاء...' : 'إلغاء الطلب'}
            </button>
            <button onClick={() => navigate(`/customer/orders/${orderId}`)}
              className="wt-btn-ghost" style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--wt-radius-md)', fontSize: '0.825rem' }}>
              عرض تفاصيل الطلب
            </button>
          </div>
          <style>{`@keyframes wt-spin { to{transform:rotate(360deg)} }`}</style>
        </div>
      );
    }

    /* ── Still searching for offers ── */
    const fillPct = Math.min(90, 15 + (waitSecs / 300) * 75);
    const phase = waitSecs < 20 ? 0 : waitSecs < 80 ? 1 : 2;
    const phaseTitle = ['نبحث عن أفضل عرض لك', 'العروض في الطريق إليك', 'لحظات وتصلك العروض'][phase];
    const phaseMsg = [
      'يتم إرسال طلبك للمزودين القريبين منك...',
      'جارٍ استقبال العروض — سيصلك إشعار فور وصول عرض',
      'ستظهر العروض قريباً — يمكنك متابعة التفاصيل',
    ][phase];

    return (
      <div style={{ minHeight: '65vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '1rem', padding: '1.5rem', fontFamily: "'Cairo', sans-serif" }}>
        <style>{`
          @keyframes wt-icon-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
          @keyframes wt-ring { 0%{transform:translate(-50%,-50%) scale(0.7);opacity:0.6} 100%{transform:translate(-50%,-50%) scale(2.0);opacity:0} }
          @keyframes wt-drp { 0%{transform:translateY(0);opacity:0} 20%{opacity:1} 100%{transform:translateY(32px);opacity:0} }
          @keyframes wt-bc1 { 0%,100%{opacity:0.15} 33%{opacity:0.9} }
          @keyframes wt-bc2 { 0%,100%{opacity:0.15} 33%{opacity:0.9} }
          @keyframes wt-bc3 { 0%,100%{opacity:0.15} 33%{opacity:0.9} }
        `}</style>

        {/* Outer wrapper — fixed size so everything is centered */}
        <div style={{ position:'relative', width:160, height:140 }}>


          {/* Icon circle */}
          <div style={{
            position:'absolute', top:20, left:30,
            width:100, height:100, borderRadius:'50%',
            background: isDark ? 'rgba(10,186,181,0.12)' : 'rgba(10,186,181,0.08)',
            border:`1.5px solid rgba(10,186,181,${isDark?'0.35':'0.4'})`,
            display:'flex', alignItems:'center', justifyContent:'center',
            animation:'wt-icon-pulse 2.8s ease-in-out infinite',
            overflow:'hidden',
          }}>
            <svg width="140" height="140" viewBox="0 0 24 24" fill="none"
              stroke={isDark ? '#0ABAB5' : '#0284c7'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7"/>
              <path d="M16.5 16.5L21 21"/>
              <path d="M8 11h6M11 8v6" strokeOpacity="0.6"/>
            </svg>
          </div>

          {/* Water drops — fall from top of icon */}
          {[
            {left:52, delay:'0s'},
            {left:72, delay:'0.7s'},
            {left:92, delay:'1.4s'},
          ].map((d,i) => (
            <div key={i} style={{
              position:'absolute', top:12, left:d.left,
              width:5, height:9,
              borderRadius:'50% 50% 55% 55%',
              background:'rgba(10,186,181,0.75)',
              animation:`wt-drp 1.9s ease-in ${d.delay} infinite`,
            }}/>
          ))}

          {/* Broadcast arcs — both sides */}
          <svg style={{position:'absolute',top:0,left:0,pointerEvents:'none',overflow:'visible'}}
            width="160" height="160" viewBox="0 0 160 160" fill="none">
            {/* RIGHT arcs */}
            <path d="M 108 65 A 18 18 0 0 1 108 95" stroke="#0ABAB5" strokeWidth="2.5" strokeLinecap="round"
              style={{animation:'wt-bc1 1.4s ease-in-out 0s infinite'}}/>
            <path d="M 118 55 A 32 32 0 0 1 118 105" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"
              style={{animation:'wt-bc2 1.4s ease-in-out 0.32s infinite'}}/>
            <path d="M 129 44 A 46 46 0 0 1 129 116" stroke="#0ABAB5" strokeWidth="1.5" strokeLinecap="round"
              style={{animation:'wt-bc3 1.4s ease-in-out 0.64s infinite'}}/>
            {/* LEFT arcs — mirrored */}
            <path d="M 52 65 A 18 18 0 0 0 52 95" stroke="#0ABAB5" strokeWidth="2.5" strokeLinecap="round"
              style={{animation:'wt-bc1 1.4s ease-in-out 0s infinite'}}/>
            <path d="M 42 55 A 32 32 0 0 0 42 105" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"
              style={{animation:'wt-bc2 1.4s ease-in-out 0.32s infinite'}}/>
            <path d="M 31 44 A 46 46 0 0 0 31 116" stroke="#0ABAB5" strokeWidth="1.5" strokeLinecap="round"
              style={{animation:'wt-bc3 1.4s ease-in-out 0.64s infinite'}}/>
          </svg>

        </div>

        {/* Text */}
        <div style={{ maxWidth: 300 }}>
          <h2 style={{ color: 'var(--wt-text-primary)', fontWeight: 900, fontSize: '1.2rem', margin: '0 0 0.45rem' }}>
            {phaseTitle}
          </h2>
          <p style={{ color: 'var(--wt-text-muted)', fontSize: '0.82rem', margin: 0, lineHeight: 1.7 }}>
            {phaseMsg}
          </p>
        </div>

        {/* Timer pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.4rem 1rem', borderRadius: 999,
          background: 'var(--wt-surface)', border: '1px solid var(--wt-border)',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--wt-cyan-bright)" strokeWidth={2}>
            <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 6v6l4 2"/>
          </svg>
          <span style={{ color: 'var(--wt-cyan-bright)', fontWeight: 700, fontSize: '0.8rem' }}>
            {Math.floor(waitSecs / 60) > 0 ? `${Math.floor(waitSecs/60)} د ` : ''}{waitSecs % 60} ث
          </span>
          <span style={{ color: 'var(--wt-text-muted)', fontSize: '0.73rem' }}>انتظار</span>
        </div>

        <button onClick={() => { clearInterval(pollRef.current); navigate(`/customer/orders/${orderId}`); }}
          style={{ color: 'var(--wt-text-muted)', fontSize: '0.77rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          عرض تفاصيل الطلب
        </button>
      </div>
    );
  }

  /* ═══════════════ LOADING ═══════════════ */
  if (loading) return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl animate-pulse" style={{ background: 'var(--wt-surface)' }} />
        <div className="flex-1 space-y-1.5">
          <div className="h-4 rounded-lg w-24 animate-pulse" style={{ background: 'var(--wt-surface)' }} />
          <div className="h-3 rounded-lg w-16 animate-pulse" style={{ background: 'var(--wt-surface)' }} />
        </div>
      </div>
      {[1,2,3].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: 'var(--wt-surface)' }} />)}
    </div>
  );

  /* ═══════════════ MAIN RENDER ═══════════════ */
  return (
    <div className="flex flex-col gap-4">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}
          className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-95"
          style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-secondary)' }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-black" style={{ color: 'var(--wt-text-primary)' }}>طلب جديد</h1>
          <p className="text-xs font-medium" style={{ color: 'var(--wt-text-muted)' }}>{STEPS[step]}</p>
        </div>
        {/* Step counter */}
        <div className="flex items-center justify-center w-10 h-10 rounded-2xl text-xs font-black"
          style={{ background: 'linear-gradient(135deg, rgba(10,186,181,0.2), rgba(0,102,204,0.15))', border: '1px solid rgba(10,186,181,0.3)', color: '#0ABAB5' }}>
          {step+1}/{STEPS.length}
        </div>
      </div>

      {/* ── Step Progress ── */}
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div
                className="w-7 h-7 rounded-xl flex items-center justify-center text-[11px] font-black transition-all duration-300"
                style={{
                  background: i < step
                    ? 'linear-gradient(135deg, #0ABAB5, #0066CC)'
                    : i === step
                    ? 'linear-gradient(135deg, rgba(10,186,181,0.2), rgba(0,102,204,0.15))'
                    : 'var(--wt-surface)',
                  border: i <= step ? '1px solid rgba(10,186,181,0.5)' : '1px solid var(--wt-border)',
                  color: i < step ? '#fff' : i === step ? '#0ABAB5' : 'var(--wt-text-muted)',
                  boxShadow: i === step ? '0 0 12px rgba(10,186,181,0.3)' : 'none',
                }}
              >
                {i < step ? (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                ) : i + 1}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 rounded-full transition-all duration-500"
                style={{ background: i < step ? 'linear-gradient(90deg, #0ABAB5, #0066CC)' : 'var(--wt-border)' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ════════════ STEP 0: WATER TYPE ════════════ */}
      {step === 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold" style={{ color: 'var(--wt-text-muted)' }}>اختر نوع المياه</p>
          <div className="grid grid-cols-2 gap-3">
            {waterTypes.map(wt => {
              const sel = waterTypeId === wt.id;
              const color = wt.color || '#0ABAB5';
              return (
                <button
                  key={wt.id}
                  onClick={() => { setWaterTypeId(wt.id); setTimeout(() => setStep(1), 220); }}
                  className="relative flex flex-col justify-between p-2.5 rounded-2xl text-right transition-all duration-200 active:scale-[0.96] overflow-hidden"
                  style={{
                    minHeight: 110,
                    background: sel ? `linear-gradient(135deg, ${color}cc, ${color}88)` : 'var(--wt-surface)',
                    border: sel ? `1.5px solid ${color}` : '1px solid var(--wt-border)',
                    boxShadow: sel ? `0 0 20px ${color}40, 0 4px 16px rgba(0,0,0,0.15)` : 'none',
                  }}
                >
                  {/* أيقونة في الزاوية العلوية */}
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: sel ? 'rgba(255,255,255,0.18)' : `${color}18`, border: `1px solid ${color}30` }}>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={sel ? 'white' : color}>
                      <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z"/>
                    </svg>
                  </div>
                  {/* checkmark */}
                  {sel && (
                    <span className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.25)' }}>
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                    </span>
                  )}
                  {/* نص أسفل البطاقة */}
                  <div>
                    <p className="text-xs font-black leading-tight mb-0.5" style={{ color: sel ? '#fff' : 'var(--wt-text-primary)' }}>{wt.name_ar}</p>
                    {wt.description && (
                      <p className="text-[9px] leading-snug line-clamp-2" style={{ color: sel ? 'rgba(255,255,255,0.7)' : 'var(--wt-text-muted)' }}>{wt.description}</p>
                    )}
                    {wt.is_drinkable && (
                      <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                        style={sel ? { background: 'rgba(255,255,255,0.2)', color: '#fff' } : { background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}>
                        ✓ صالح للشرب
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════════ STEP 1: VEHICLE CATEGORY ════════════ */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-xs font-bold" style={{ color: 'var(--wt-text-muted)' }}>الكميات بحسب فئات المركبات المتوفرة</p>

          {vehicleCategories.length === 0 ? (
            <div className="rounded-2xl p-4 text-center" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
              <p className="text-sm font-semibold" style={{ color: '#fbbf24' }}>لا توجد فئات مركبات متاحة حالياً</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {vehicleCategories.map(cat => {
                const sel = selCategory?.id === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelCategory(cat)}
                    className="flex flex-col items-center py-3 px-2 rounded-2xl font-bold transition-all duration-200 active:scale-95"
                    style={{
                      background: sel ? 'linear-gradient(135deg, #0ABAB5cc, #0066CCaa)' : 'var(--wt-surface)',
                      border: sel ? '1.5px solid #0ABAB5' : '1px solid var(--wt-border)',
                      boxShadow: sel ? '0 0 20px rgba(10,186,181,0.35), 0 4px 16px rgba(0,0,0,0.15)' : 'none',
                    }}
                  >
                    <div style={{ width: 56, height: 44, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="52" height="44" viewBox="0 0 24 24" fill="none"
                        stroke={sel ? 'white' : isDark ? '#0ABAB5' : '#0284c7'}
                        strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="8" width="15" height="10" rx="1"/>
                        <path d="M16 10h4l2 4v4h-6V10z"/>
                        <circle cx="5.5" cy="18.5" r="1.5" fill={sel ? 'white' : isDark ? '#0ABAB5' : '#0284c7'} stroke="none"/>
                        <circle cx="18.5" cy="18.5" r="1.5" fill={sel ? 'white' : isDark ? '#0ABAB5' : '#0284c7'} stroke="none"/>
                        <path d="M4 8V6a2 2 0 012-2h6a2 2 0 012 2v2" strokeOpacity="0.6"/>
                      </svg>
                    </div>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-xl font-black" style={{ color: sel ? '#fff' : 'var(--wt-text-primary)' }}>{cat.capacity_tons}</span>
                      <span className="text-xs font-bold" style={{ color: sel ? 'rgba(255,255,255,0.8)' : 'var(--wt-text-muted)' }}>طن</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {selCategory && (
            <div className="rounded-2xl p-3.5 flex items-center gap-3"
              style={{ background: 'rgba(10,186,181,0.08)', border: '1px solid rgba(10,186,181,0.2)' }}>
              <svg className="w-7 h-7 shrink-0" viewBox="0 0 24 24" fill="#0ABAB5"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z"/></svg>
              <div>
                <p className="text-sm font-bold" style={{ color: '#0ABAB5' }}>{selectedWaterType?.name_ar} — {selCategory.name}</p>
                <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>{selCategory.capacity_tons} طن = {(selCategory.capacity_tons * 1000).toLocaleString('ar')} لتر</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold mb-2" style={{ color: 'var(--wt-text-muted)' }}>ملاحظات للسائق (اختياري)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="مثال: اطرق الباب الجانبي..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl resize-none focus:outline-none transition-all text-sm"
              style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-primary)' }}
            />
          </div>
        </div>
      )}

      {/* ════════════ STEP 2: LOCATION ════════════ */}
      {step === 2 && (
        <div className="space-y-3">
          <p className="text-xs font-bold" style={{ color: 'var(--wt-text-muted)' }}>انقر على الخريطة أو استخدم GPS</p>

          {/* ── MAP ── */}
          <GoogleMapPicker
            initialPos={mapPos}
            onPick={handlePick}
            height={300}
          />

          {/* ── Selected address display ── */}
          {(mapPos || siteId) && (
            <div className="rounded-xl p-3" style={{
              background: siteId ? 'rgba(10,186,181,0.08)' : 'rgba(16,185,129,0.08)',
              border: siteId ? '1px solid rgba(10,186,181,0.25)' : '1px solid rgba(16,185,129,0.25)',
            }}>
              <p className="font-bold text-xs mb-0.5" style={{ color: siteId ? '#0ABAB5' : '#10b981' }}>
                📍 {siteId ? 'عنوان محفوظ' : 'موقع محدد على الخريطة'}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--wt-text-secondary)' }}>
                {siteId ? (sites.find(s => s.id === siteId)?.name + ' — ' + (sites.find(s => s.id === siteId)?.formatted_address || '')) : geocodedAddr}
              </p>
            </div>
          )}

          {/* ── Quick Next after location selected ── */}
          {(mapPos || siteId) && (
            <div className="flex justify-center">
              <button
                onClick={() => setStep(3)}
                className="relative flex items-center gap-2 px-8 py-3 rounded-2xl text-white font-black text-sm active:scale-[0.96] transition-all overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #0ABAB5, #0066CC)',
                  boxShadow: '0 4px 24px rgba(10,186,181,0.45), 0 1px 0 rgba(255,255,255,0.15) inset',
                  minWidth: 140,
                }}
              >
                <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                  <svg viewBox="0 0 300 48" preserveAspectRatio="none"
                    style={{ position:'absolute', inset:0, width:'100%', height:'100%',
                      opacity:0.12, animation:'wt-fill-wave 3s ease-in-out infinite' }}>
                    <path d="M0,24 C40,10 80,38 120,24 C160,10 200,38 240,24 C270,14 300,24 300,24 L300,48 L0,48 Z" fill="white"/>
                  </svg>
                </span>
                <span className="relative z-10">التالي</span>
                <svg className="relative z-10 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                  style={{ animation:'wt-arrow-bounce 1s ease-in-out infinite' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          )}

          {/* ── Save address prompt ── */}
          {isNewLocation && !addrSaved && (
            <div className="rounded-2xl p-4 space-y-3"
              style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>
              <p className="text-xs font-bold" style={{ color: 'var(--wt-text-secondary)' }}>💾 حفظ هذا العنوان للمرات القادمة؟</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={saveAddrName}
                  onChange={e => setSaveAddrName(e.target.value)}
                  placeholder="اسم العنوان (مثال: البيت، العمل...)"
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm focus:outline-none transition-all"
                  style={{ background: 'var(--wt-bg-2)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-primary)' }}
                />
                <button
                  onClick={saveAddress}
                  disabled={!saveAddrName.trim() || savingAddr}
                  className="px-4 py-2 rounded-xl text-white text-xs font-bold transition-all disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)' }}
                >
                  {savingAddr ? '...' : 'حفظ'}
                </button>
              </div>
            </div>
          )}
          {addrSaved && (
            <div className="rounded-xl px-3 py-2.5 text-xs font-bold"
              style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981' }}>
              ✓ تم حفظ العنوان — يمكنك استخدامه في طلباتك القادمة
            </div>
          )}

          {/* ── Saved addresses ── */}
          {sites.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold" style={{ color: 'var(--wt-text-muted)' }}>العناوين المحفوظة</p>
              {sites.map(site => (
                <button
                  key={site.id}
                  onClick={() => selectSite(site)}
                  className="w-full text-right flex items-start gap-3 px-3 py-3 rounded-xl transition-all"
                  style={{
                    background: siteId === site.id ? 'rgba(10,186,181,0.08)' : 'var(--wt-surface)',
                    border: siteId === site.id ? '1px solid rgba(10,186,181,0.4)' : '1px solid var(--wt-border)',
                  }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: siteId === site.id ? '#0ABAB5' : 'rgba(10,186,181,0.12)',
                      color: siteId === site.id ? '#fff' : '#0ABAB5',
                    }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>{site.name}</p>
                      {site.is_default && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(10,186,181,0.15)', color: '#0ABAB5' }}>افتراضي</span>
                      )}
                    </div>
                    <p className="text-xs truncate mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>{site.formatted_address || site.address_line1 || ''}</p>
                  </div>
                  {siteId === site.id && (
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ╔══════════ STEP 3: PRICE + CONFIRM ══════════ */}
      {step === 3 && (
        <div className="space-y-4">
          {/* ── Price Card ── */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--wt-border)' }}>
            <div className="px-5 pt-5 pb-4 text-center"
              style={{ background: 'linear-gradient(135deg, #0a2540, #0d3060 50%, #0ABAB5)' }}>
              <p className="text-white/60 text-[10px] font-medium tracking-widest uppercase mb-1">سعر تقريبي — يتحدد بعد عروض المزودين</p>
              <p className="text-white font-bold text-base">{selectedWaterType?.name_ar} — {selCategory?.capacity_tons} طن</p>
            </div>

            {priceLoading ? (
              <div className="p-5 space-y-3" style={{ background: 'var(--wt-surface)' }}>
                {[1,2,3].map(i => <div key={i} className="h-4 rounded-lg animate-pulse" style={{ background: 'var(--wt-bg-2)' }} />)}
              </div>
            ) : estimate ? (
              estimate.configured === false ? (
                <div className="p-5 text-center space-y-2" style={{ background: 'var(--wt-surface)' }}>
                  <p className="text-2xl">⚙️</p>
                  <p className="text-sm font-semibold" style={{ color: '#fbbf24' }}>التسعير غير مُعدّ بعد</p>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>{estimate.note}</p>
                </div>
              ) : (() => {
                const preTax = (estimate.subtotal || 0) + (estimate.delivery_fee || 0);
                return (
                  <div className="px-5 pt-4 pb-5 space-y-3" style={{ background: 'var(--wt-surface)' }}>
                    {[['السعر', preTax], [`ضريبة القيمة المضافة (${estimate.tax_rate}%)`, estimate.tax_amount]].map(([label, val]) => (
                      <div key={label} className="flex justify-between items-center text-sm">
                        <span style={{ color: 'var(--wt-text-muted)' }}>{label}</span>
                        <span className="font-semibold" style={{ color: 'var(--wt-text-primary)' }}>{fmtSAR(val)}{' '}<SAR className="h-[0.85em] w-auto" /></span>
                      </div>
                    ))}
                    <div className="pt-3 flex justify-between items-center" style={{ borderTop: '2px dashed var(--wt-border)' }}>
                      <span className="font-bold text-sm" style={{ color: 'var(--wt-text-secondary)' }}>الإجمالي</span>
                      <span className="text-2xl font-black" style={{ color: '#0ABAB5' }}>{fmtSAR(estimate.total)}{' '}<SAR className="h-[0.75em] w-auto" /></span>
                    </div>
                    {estimate.note && (
                      <p className="text-[10px] text-center pt-1" style={{ color: 'var(--wt-text-muted)' }}>⚠ {estimate.note}</p>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="p-5 text-center" style={{ background: 'var(--wt-surface)' }}>
                <p className="text-sm" style={{ color: 'var(--wt-text-muted)' }}>تعذّر تحميل التسعير</p>
                <button onClick={() => {
                  setPriceLoading(true);
                  const p = new URLSearchParams({ water_type_id: waterTypeId, quantity_tons: selCategory.capacity_tons });
                  apiCall(`/customer/catalog/estimate?${p}`).then(setEstimate).catch(()=>{}).finally(()=>setPriceLoading(false));
                }} className="mt-2 text-xs underline" style={{ color: '#0ABAB5' }}>إعادة المحاولة</button>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--wt-border)', background: 'var(--wt-surface)' }}>
            {[
              { label: 'نوع المياه',    value: selectedWaterType?.name_ar || '—' },
              { label: 'فئة المركبة',   value: selCategory ? `${selCategory.name} (${selCategory.capacity_tons} طن)` : '—' },
              { label: 'موقع التوصيل', value: siteId ? (sites.find(s => s.id === siteId)?.name || '—') : (geocodedAddr || '—') },
              notes && { label: 'ملاحظات', value: notes },
            ].filter(Boolean).map(({ label, value }, i, arr) => (
              <div key={label} className="flex justify-between items-start px-4 py-3 gap-3"
                style={{ borderBottom: i < arr.length-1 ? '1px solid var(--wt-border)' : 'none' }}>
                <span className="text-xs shrink-0" style={{ color: 'var(--wt-text-muted)' }}>{label}</span>
                <span className="text-sm font-medium text-left" style={{ color: 'var(--wt-text-primary)' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Payment method */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--wt-border)', background: 'var(--wt-surface)' }}>
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--wt-border)' }}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={2}><rect x="2" y="5" width="20" height="14" rx="2"/><path strokeLinecap="round" d="M2 10h20"/></svg>
              <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>طريقة الدفع</p>
            </div>
            <div className="p-3 space-y-2">
              {[{ id: 'cash', label: 'نقداً', sub: 'يُسلّم للسائق عند التسليم',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" d="M2 7h20M2 12h20M6 17h12"/><rect x="2" y="5" width="20" height="14" rx="2"/></svg> },
                { id: 'card', label: 'دفع إلكتروني', sub: 'Visa · Mastercard · مدى · Apple Pay',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><rect x="2" y="5" width="20" height="14" rx="2"/><path strokeLinecap="round" d="M2 10h20"/><path strokeLinecap="round" d="M6 15h3"/></svg> },
              ].map(({ id, label, sub, icon }) => {
                const sel = paymentMethod === id;
                return (
                  <button key={id} type="button" onClick={() => setPaymentMethod(id)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-right"
                    style={{
                      background: sel ? 'rgba(10,186,181,0.08)' : 'var(--wt-bg-2)',
                      border: sel ? '1.5px solid rgba(10,186,181,0.5)' : '1px solid var(--wt-border)',
                    }}>
                    <span style={{ color: sel ? '#0ABAB5' : 'var(--wt-text-muted)' }}>{icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>{label}</p>
                      <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>{sub}</p>
                    </div>
                    {sel && (
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                    )}
                  </button>
                );
              })}

              {/* Wallet */}
              {(() => {
                const orderTotal = estimate?.total || 0;
                const hasSufficient = walletBalance !== null && walletBalance >= orderTotal;
                const isSelected = paymentMethod === 'wallet';
                return (
                  <button type="button" onClick={() => hasSufficient ? setPaymentMethod('wallet') : setCheckoutOpen(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-right"
                    style={{
                      background: isSelected ? 'rgba(16,185,129,0.08)' : 'var(--wt-bg-2)',
                      border: isSelected ? '1.5px solid rgba(16,185,129,0.5)' : '1px solid var(--wt-border)',
                    }}>
                    <img src={walletLogo} alt="محفظة" style={{ width:'1.25rem', height:'1.25rem', objectFit:'contain', transform:'scale(1.45)', filter: isDark ? 'invert(60%) sepia(80%) saturate(400%) hue-rotate(150deg) brightness(1.2)' : 'invert(35%) sepia(60%) saturate(500%) hue-rotate(150deg) brightness(0.9)' }} />
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>المحفظة</p>
                      <p className="text-xs" style={{ color: walletBalance !== null && !hasSufficient ? '#fbbf24' : 'var(--wt-text-muted)' }}>
                        {walletBalance !== null ? <>الرصيد: {Number(walletBalance).toFixed(2)} <SAR className="h-[0.85em] w-auto" /></> : 'جاري تحميل...'}
                        {walletBalance !== null && !hasSufficient && ' — غير كافٍ'}
                      </p>
                    </div>
                    {isSelected ? (
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                    ) : !hasSufficient && walletBalance !== null ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>شحن</span>
                    ) : null}
                  </button>
                );
              })()}
            </div>

            {paymentMethod === 'wallet' && (
              <div className="px-4 pb-3">
                <div className="rounded-xl px-3 py-2 text-xs font-medium"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981' }}>
                  ✓ سيُخصم {estimate?.total ? <>{Number(estimate.total).toFixed(2)}{' '}<SAR className="h-[0.85em] w-auto" /></> : ''} من رصيد محفظتك فور تأكيد الطلب
                </div>
              </div>
            )}
            {paymentMethod === 'card' && (
              <div className="px-4 pb-3">
                <div className="rounded-xl px-3 py-2 text-xs"
                  style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)', color: 'var(--wt-cyan-bright)' }}>
                  سيتم تحويلك لبوابة الدفع الآمنة عند تأكيد الطلب
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Wallet Top-up CheckoutSheet ── */}
      <CheckoutSheet
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={(newBal) => {
          setCheckoutOpen(false);
          setShowTopup(false);
          if (newBal !== undefined) setWalletBalance(newBal);
        }}
      />

      {/* ── Action Button ── */}
      <div className="pt-2 pb-2 flex justify-center">
        {step < 3 ? (
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canNext()}
            className="relative flex items-center gap-2 px-8 py-3 rounded-2xl text-white font-black text-sm active:scale-[0.96] transition-all disabled:opacity-35 disabled:cursor-not-allowed overflow-hidden"
            style={{
              background: canNext() ? 'linear-gradient(135deg, #0ABAB5, #0066CC)' : 'var(--wt-surface)',
              boxShadow: canNext() ? '0 4px 24px rgba(10,186,181,0.45), 0 1px 0 rgba(255,255,255,0.15) inset' : 'none',
              border: canNext() ? 'none' : '1px solid var(--wt-border)',
              minWidth: 140,
            }}
          >
            <style>{`
              @keyframes wt-fill-wave {
                0%   { transform: translateX(0) translateY(0); }
                50%  { transform: translateX(-12%) translateY(-2px); }
                100% { transform: translateX(0) translateY(0); }
              }
              @keyframes wt-arrow-bounce {
                0%, 100% { transform: translateX(0); }
                50%       { transform: translateX(4px); }
              }
            `}</style>
            {/* موجة مملئة خفيفة */}
            {canNext() && (
              <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                <svg viewBox="0 0 300 48" preserveAspectRatio="none"
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%',
                    opacity:0.12, animation:'wt-fill-wave 3s ease-in-out infinite' }}>
                  <path d="M0,24 C40,10 80,38 120,24 C160,10 200,38 240,24 C270,14 300,24 300,24 L300,48 L0,48 Z" fill="white"/>
                </svg>
              </span>
            )}
            <span className="relative z-10">التالي</span>
            <svg className="relative z-10 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
              style={{ animation: canNext() ? 'wt-arrow-bounce 1s ease-in-out infinite' : 'none' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        ) : estimate?.configured === false ? (
          <div className="w-full rounded-2xl px-5 py-4 text-center"
            style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.25)' }}>
            <p className="text-sm font-bold" style={{ color: '#f43f5e' }}>⚠️ عذراً، لا يمكن إكمال الطلب</p>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>لم يتم تكوين أسعار الخدمة بعد.</p>
          </div>
        ) : (
          <button
            onClick={submit}
            disabled={submitting || !estimate || priceLoading || !estimate?.total}
            className="relative flex items-center gap-2 px-8 py-3 rounded-2xl text-white font-black text-sm active:scale-[0.96] transition-all disabled:opacity-35 disabled:cursor-not-allowed overflow-hidden"
            style={{
              background: (!submitting && estimate && !priceLoading && estimate?.total) ? 'linear-gradient(135deg, #0ABAB5, #0066CC)' : 'var(--wt-surface)',
              boxShadow: (!submitting && estimate && !priceLoading && estimate?.total) ? '0 4px 24px rgba(10,186,181,0.45), 0 1px 0 rgba(255,255,255,0.15) inset' : 'none',
              border: (!submitting && estimate && !priceLoading && estimate?.total) ? 'none' : '1px solid var(--wt-border)',
              minWidth: 160,
            }}
          >
            {/* موجة مملئة خفيفة */}
            {!submitting && estimate && !priceLoading && estimate?.total && (
              <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                <svg viewBox="0 0 300 48" preserveAspectRatio="none"
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%',
                    opacity:0.12, animation:'wt-fill-wave 3s ease-in-out infinite' }}>
                  <path d="M0,24 C40,10 80,38 120,24 C160,10 200,38 240,24 C270,14 300,24 300,24 L300,48 L0,48 Z" fill="white"/>
                </svg>
              </span>
            )}
            {submitting ? (
              <><span className="relative z-10 w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span className="relative z-10">جاري الإرسال...</span></>
            ) : priceLoading || !estimate ? (
              <><span className="relative z-10 w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span className="relative z-10">يحسب السعر...</span></>
            ) : (
              <>
                <svg className="relative z-10 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                <span className="relative z-10">طلب عروض المزودين</span>
                <svg className="relative z-10 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                  style={{ animation:'wt-arrow-bounce 1s ease-in-out infinite' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
